import { DecimalPipe } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DocumentChunk } from '@reflact/docsearch';
import dayjs from 'dayjs';
import { firstValueFrom } from 'rxjs';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MessageModule } from 'primeng/message';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
// eslint-disable-next-line no-restricted-imports
import { HttpClient } from '@angular/common/http';
import { SelectableDocument } from 'src/app/shared/services/file-upload.service';

type SearchResultItem = { document: unknown; chunk: { vs_score: number } & Record<string, unknown> };

@Component({
    selector: 'app-project-document-detail',
    standalone: true,
    imports: [RouterLink, DecimalPipe,
        AccordionModule,
        ButtonModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule,
        MessageModule,
        TableModule,
        TagModule
    ],
    providers: [DecimalPipe],
    templateUrl: './project-document-detail.component.html'
})
export class ProjectDocumentDetailComponent implements OnChanges {
    @Input() public doc!: SelectableDocument;
    public readonly chunks = input<DocumentChunk[]>([]);
    public readonly chunksLoading = input(false);

    public dayjs = dayjs;
    private readonly _searchIsRunning = signal(false);
    public get searchIsRunning(): boolean { return this._searchIsRunning(); }
    public set searchIsRunning(value: boolean) { this._searchIsRunning.set(value); }
    private readonly _searchResults = signal<SearchResultItem[]>([]);
    public get searchResults(): SearchResultItem[] { return this._searchResults(); }
    public set searchResults(value: SearchResultItem[]) { this._searchResults.set(value); }
    private readonly _searchCompleted = signal(false);
    public get searchCompleted(): boolean { return this._searchCompleted(); }
    public set searchCompleted(value: boolean) { this._searchCompleted.set(value); }

    constructor(private http: HttpClient) { }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['doc']) {
            this.resetSearch();
        }
    }

    public resetSearch(): void {
        this.searchResults = [];
        this.searchCompleted = false;
    }

    public async runSearchTest(phrase: string): Promise<void> {
        if (!phrase.trim()) return;
        this.searchIsRunning = true;
        this.searchCompleted = false;
        this.searchResults = [];
        try {
            const result = await firstValueFrom(
                this.http.post<{ searchResults: SearchResultItem[] }>(
                    `/api/toolbox/bot/admin/projectdoc/searchtest/${this.doc._id}`,
                    { phrase }
                )
            );
            this.searchResults = result.searchResults;
        } finally {
            this.searchIsRunning = false;
            this.searchCompleted = true;
        }
    }

    public getMimeTypeIcon(mimetype: string): string {
        if (!mimetype) return 'ri-file-line';
        if (mimetype === 'application/pdf') return 'ri-file-pdf-2-line';
        if (mimetype.startsWith('image/')) return 'ri-image-line';
        if (mimetype.startsWith('audio/')) return 'ri-file-music-line';
        if (mimetype.startsWith('video/')) return 'ri-video-line';
        if (
            mimetype === 'application/msword' ||
            mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ) return 'ri-file-word-2-line';
        if (
            mimetype === 'application/vnd.ms-excel' ||
            mimetype === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) return 'ri-file-excel-2-line';
        if (
            mimetype === 'application/vnd.ms-powerpoint' ||
            mimetype === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        ) return 'ri-file-ppt-2-line';
        if (mimetype === 'text/plain' || mimetype === 'text/csv') return 'ri-file-text-line';
        return 'ri-file-line';
    }
}
